import type { AgentReportMetrics } from 'constants/agentReporting';
import { DateTime } from 'luxon';
import type { UserSummaryByDay } from 'redux/modules/summaries.types';
import type { CommonDataEntity } from 'redux/modules/superreports.types';
import { getHistoricCapacityByDate } from 'redux/selectors/utils/capacities';
import type { Currency } from 'types/account';
import type { DurationSummary, RawCapacity } from 'types/summaries';
import { DurationSummaryKeys } from 'types/summaries';
import type { Duration } from 'types/units';
import { getDaysInRange } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { DEFAULT_WEEKDAYS } from 'utils/work-days';

export interface DurationSummaryParams {
  duration: Duration;
  billable_duration: Duration;
  non_billable_duration: Duration;
  total_duration: Duration;
  capacity: Duration;
  total_capacity: Duration;
  planned_duration: Duration;
  tasked_duration: Duration;
}

const defaultDurationValues = {
  duration: 0,
  billable_duration: 0,
  non_billable_duration: 0,
  total_duration: 0,
  capacity: 0,
  total_capacity: 0,
  planned_duration: 0,
  tasked_duration: 0,
};

export const durationSummary = (
  summary: Partial<DurationSummaryParams> | null
): DurationSummary => {
  if (summary == null) {
    return defaultDurationValues;
  }

  return DurationSummaryKeys.reduce((previous: DurationSummary, key) => {
    const summaryByKey = summary[key];
    if (!summaryByKey) {
      return previous;
    }
    return {
      ...previous,
      [key]: Math.round((summaryByKey.total_minutes / 60) * 100) / 100 || 0,
    };
  }, defaultDurationValues);
};

export const getDaysInRangeAsHash = (range: Array<string>, capacityHistory: Array<RawCapacity>) => {
  return range.reduce((hash, day) => {
    const capacity =
      capacityHistory != null ? getHistoricCapacityByDate(capacityHistory, day) : null;

    return {
      ...hash,
      [day]: {
        ...defaultDurationValues,
        capacity: capacity?.daily_capacity || 0,
        total_capacity: capacity?.weekly_capacity || 0,
      },
    };
  }, {});
};

export const controlSummary = (
  summary: UserSummaryByDay | null,
  from: string | undefined,
  to: string | undefined,
  userCapacityHistory: Array<RawCapacity>
): Record<string, DurationSummaryParams> | null => {
  if (!summary || !summary.by_days) {
    return null;
  }

  return summary.by_days.reduce(
    (days, day) => {
      return {
        ...days,
        [day.day]: {
          day: day.day,
          ...durationSummary(day),
        },
      };
    },
    getDaysInRangeAsHash(
      getDaysInRange(from ?? DateTime.now().toISODate(), to ?? DateTime.now().toISODate()),
      userCapacityHistory
    )
  );
};

interface UserSummaryReturnType extends DurationSummary {
  control: Record<string, DurationSummaryParams> | null;
}
export const userSummary = (
  summary: UserSummaryByDay | null,
  from: string | undefined,
  to: string | undefined,
  userCapacityHistory: Array<RawCapacity>
): UserSummaryReturnType => {
  if (summary == null) {
    return {
      ...durationSummary(null),
      control: controlSummary(null, from, to, userCapacityHistory),
    };
  }

  return {
    ...durationSummary(summary),
    control: controlSummary(summary, from, to, userCapacityHistory),
  };
};

export { DEFAULT_WEEKDAYS, DEFAULT_WEEKDAYS_LIST } from 'utils/work-days';

export const createDefaultUserCapacities = (
  weeklyCapacity: number,
  workDays: string = DEFAULT_WEEKDAYS
): Array<RawCapacity> => {
  const daysPerWeek = workDays.split(',').length;
  return [
    {
      current: true,
      daily_capacity: weeklyCapacity / daysPerWeek,
      end_date: null,
      id: null,
      start_date: '1970-01-01',
      total_working_days: null,
      work_days: workDays,
      weekly_capacity: weeklyCapacity,
      weekly_working_days: daysPerWeek,
    },
  ];
};

export interface DayRangeInfo {
  date: string;
  dayInMonth: number;
  isToday: boolean;
  isWeekend: boolean;
  weekNumber: number;
  monthName: string;
  isAfterToday: boolean;
}

export function formatDayRange(
  from: string | undefined,
  to: string | undefined
): Array<DayRangeInfo> {
  const today = DateTime.now().toISODate();
  const days = getDaysInRange(from ?? today, to ?? today);
  return days.map(day => {
    const date = fromISO(day);
    return {
      date: day,
      dayInMonth: +date.toFormat('d'), // Do
      isToday: today === day,
      isWeekend: date.weekday === 6 || date.weekday === 7,
      weekNumber: date.weekNumber,
      monthName: date.toFormat('MMM'),
      isAfterToday: date.toISODate() > today,
    };
  });
}

export interface CostAndDurationSummary {
  agentActivity?: AgentReportMetrics;
  minutesLogged: number;
  minutesPlanned: number;
  minutesBilled: number;
  minutesUnbilled: number;
  minutesBillable?: number;
  minutesNonBillable?: number;
  costPlanned: number;
  costLogged: number;
  costBilled: number;
  costUnbilled: number;
  currency?: Currency;
}

export function getCostAndDurationSummary(
  entities: Array<CommonDataEntity>
): CostAndDurationSummary {
  let minutesLogged = 0;
  let minutesPlanned = 0;
  let minutesBilled = 0;
  let minutesUnbilled = 0;
  let minutesBillable = 0;
  let minutesNonBillable = 0;
  let costPlanned = 0;
  let costLogged = 0;
  let costBilled = 0;
  let costUnbilled = 0;

  entities.forEach(entity => {
    const defaultCost = { amount: 0 };
    const defaultDuration = { total_minutes: 0 };

    const cost = entity.cost ?? defaultCost;
    const estimated_cost = entity.estimated_cost ?? defaultCost;
    const billed_cost = entity.billed_cost ?? defaultCost;
    const unbilled_cost = entity.unbilled_cost ?? defaultCost;
    const duration = entity.duration ?? defaultDuration;
    const estimated_duration = entity.estimated_duration ?? defaultDuration;
    const billed_duration = entity.billed_duration ?? defaultDuration;
    const unbilled_duration = entity.unbilled_duration ?? defaultDuration;
    const billable_duration = entity.billable_duration ?? defaultDuration;
    const non_billable_duration = entity.non_billable_duration ?? defaultDuration;

    costLogged += Math.round((cost.amount ?? 0) * 100) / 100;
    costPlanned += Math.round((estimated_cost.amount ?? 0) * 100) / 100;
    costBilled += Math.round((billed_cost.amount ?? 0) * 100) / 100;
    costUnbilled += Math.round((unbilled_cost.amount ?? 0) * 100) / 100;
    minutesLogged += Math.round(duration.total_minutes * 100) / 100;
    minutesPlanned += Math.round(estimated_duration.total_minutes * 100) / 100;
    minutesBilled += Math.round(billed_duration.total_minutes * 100) / 100;
    minutesUnbilled += Math.round(unbilled_duration.total_minutes * 100) / 100;
    minutesBillable += Math.round(billable_duration.total_minutes * 100) / 100;
    minutesNonBillable += Math.round(non_billable_duration.total_minutes * 100) / 100;
  });

  return {
    minutesLogged,
    minutesPlanned,
    minutesBilled,
    minutesUnbilled,
    minutesBillable,
    minutesNonBillable,
    costPlanned,
    costLogged,
    costBilled,
    costUnbilled,
  };
}

// NB! copied & modified from utils/report.js since it'll be deleted soon.

export interface MaxCostAndDurationSummary {
  minutesLogged: number;
  costLogged: number;
}

export interface Entities {
  // any here is required, getCostAndDuration below is called with entities that does not have duration or cost.
  // we also have tests passing anything in here
  [key: number]: Partial<CommonDataEntity> & any;
}

export function getMaxCostAndDurationSummary(
  result: Array<number> = [],
  entities: Entities = {}
): MaxCostAndDurationSummary {
  return result.reduce<MaxCostAndDurationSummary>(
    (prev, entKey) => {
      const entity = entities[entKey];

      // When ie, query.filters.label_ids: [] is set, result array is read from there.
      // It may contain ids that don't exist in entities.
      if (!entity) {
        return prev;
      }

      const duration = entity.duration ? entity.duration : { total_minutes: 0 };
      const cost = entity.cost ? entity.cost : { amount: 0 };

      return {
        minutesLogged: Math.max(prev.minutesLogged, duration.total_minutes),
        costLogged: Math.max(prev.costLogged, cost.amount),
      };
    },
    {
      minutesLogged: 0,
      costLogged: 0,
    }
  );
}
